import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getTheme } from '@icarius-pages/login/selectors';
import { getQueryGroupsAction } from '@icarius-pages/queries/actions';
import { getGeoanalysisGroupsFromServer } from '@icarius-pages/geoanalysis/actions';
import { getLocalizedString } from "@icarius-localization/strings";
import AppTour from '@icarius-common/tour';
import { getCommandPaletteSteps } from './commandPalette.tour';
import { CloseIcon } from "@icarius-icons";
import {
  Dialog,
  InputBase,
  ListItem,
  ListItemText,
  Typography,
  InputAdornment,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import { Virtuoso } from 'react-virtuoso';

import useStyles from './useStyles';
import CommandPaletteRow from './commandPaletteRow';
import useCommandRoutes from './useCommandRoutes';
import useCommandSearch from './useCommandSearch';

export default function CommandPalette({ routes = [], customRoutes = [], availablePaths = [], handleOpenItem, dialogsState, handleToggleTheme }) {
  const dispatch = useDispatch();
  const themeState = useSelector(getTheme);
  const isDark = themeState?.theme === 'dark';
  const classes = useStyles({ isDark });
  const isKeyboardNav = useRef(false);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const listRef = useRef(null);

  const [forceRunTour, setForceRunTour] = useState(false);

  const formattedRoutes = useCommandRoutes({
    availablePaths,
    routes,
    customRoutes,
    handleOpenItem,
    dialogsState,
    handleToggleTheme,
    setOpen
  });

  const results = useCommandSearch(formattedRoutes, query);

  useEffect(() => {
    setSelectedIndex(0);
    if (listRef.current) {
      listRef.current.scrollToIndex({ index: 0, align: 'start' });
    }
  }, [results]);

  useEffect(() => {
    if (listRef.current && isKeyboardNav.current) {
      listRef.current.scrollToIndex({ index: selectedIndex, align: 'center' });
      isKeyboardNav.current = false;
    }
  }, [selectedIndex]);

  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(true);
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'h') {
        e.preventDefault();
        if (open) {
          setForceRunTour(true);
        }
      }
    };

    const handleOpenEvent = () => setOpen(true);

    window.addEventListener('keydown', handleGlobalKeyDown);
    window.addEventListener('open-command-palette', handleOpenEvent);

    return () => {
      window.removeEventListener('keydown', handleGlobalKeyDown);
      window.removeEventListener('open-command-palette', handleOpenEvent);
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setSelectedIndex(0);
      dispatch(getQueryGroupsAction());
      dispatch(getGeoanalysisGroupsFromServer());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, dispatch]);

  const handleSelect = useCallback((item) => {
    if (item.originalRoute && item.originalRoute.isAction) {
      item.originalRoute.action();
    } else if (handleOpenItem && item.originalRoute) {
      handleOpenItem(item.originalRoute);
    }
    setOpen(false);
  }, [handleOpenItem]);

  const handleInputKeyDown = useCallback((e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      isKeyboardNav.current = true;
      setSelectedIndex((prev) => (prev + 1) < results.length ? prev + 1 : 0);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      isKeyboardNav.current = true;
      setSelectedIndex((prev) => (prev - 1) >= 0 ? prev - 1 : Math.max(0, results.length - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      setSelectedIndex((prev) => {
        if (results[prev]) {
          handleSelect(results[prev].item);
        }
        return prev;
      });
    }
  }, [results, handleSelect]);

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      classes={{ paper: classes.dialogPaper }}
      BackdropProps={{
        classes: {
          root: classes.backdrop
        }
      }}
      maxWidth="md"
    >
      <InputBase
        id="command-palette-input"
        autoFocus
        placeholder={getLocalizedString("searchCommandsConfigurationScreens")}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={handleInputKeyDown}
        className={classes.inputBase}
        startAdornment={
          <InputAdornment position="start">
            <SearchIcon />
          </InputAdornment>
        }
        endAdornment={
          <InputAdornment position="end">
            <CloseIcon className="icon dialog-close-icon" style={{ cursor: 'pointer', color: isDark ? '#aaa' : '#757575' }} onClick={() => setOpen(false)} />
          </InputAdornment>
        }
      />

      <div id="command-palette-list" className={classes.list}>
        {results.length > 0 && (
          <Virtuoso
            ref={listRef}
            style={{ height: 400, width: '100%' }}
            data={results}
            itemContent={(index, result) => (
              <CommandPaletteRow
                key={result.item.id || index}
                result={result}
                index={index}
                selectedIndex={selectedIndex}
                handleSelect={handleSelect}
                setSelectedIndex={setSelectedIndex}
                classes={classes}
                isDark={isDark}
              />
            )}
          />
        )}

        {results.length === 0 && (
          <ListItem component="div">
            <ListItemText
              primary={
                <Typography variant="body2" align="center" style={{ padding: '20px 0', fontSize: '1rem', color: isDark ? '#fff' : '#000' }}>
                  {getLocalizedString("noResultsFoundQuery", { query })}
                </Typography>
              }
            />
          </ListItem>
        )}
      </div>

      <div id="command-palette-footer" className={classes.footer}>
        <span><kbd className={classes.keyHint}>↑</kbd> <kbd className={classes.keyHint}>↓</kbd> {getLocalizedString("toNavigate")}</span>
        <span><kbd className={classes.keyHint}>↵</kbd> {getLocalizedString("toSelect")}</span>
        <span><kbd className={classes.keyHint}>esc</kbd> {getLocalizedString("toClose")}</span>
        <span><kbd className={classes.keyHint}>ctrl</kbd><span style={{ margin: '0 4px' }}>+</span><kbd className={classes.keyHint}>h</kbd> {getLocalizedString("toHelp")}</span>
      </div>
      <AppTour
        steps={getCommandPaletteSteps()}
        tourKey="commandPaletteTourSeen"
        isOpen={open}
        forceRun={forceRunTour}
        onFinish={() => {
          setForceRunTour(false);
          setTimeout(() => {
            const input = document.getElementById('command-palette-input');
            if (input) input.focus();
          }, 100);
        }}
      />
    </Dialog>
  );
}
